import {QueryStatus, useInfiniteQuery, useQuery} from '@tanstack/react-query'
import {EndpointsItemsTypes, EndpointsSearchRequest} from 'interfaces'
import {useEffect, useMemo, useState} from 'react'
import {EndpointsService} from 'services/api'
import {transformEndpointsListResponse} from './transformer'

export const ENDPOINTS_METADATA_QUERY_KEY = ['endpoints-metadata']
export const endpointsMetadataQueryFn = () =>
  EndpointsService.getEndpointMetaData()

export const useEndpointsMetadataQuery = () => {
  return useQuery(ENDPOINTS_METADATA_QUERY_KEY, endpointsMetadataQueryFn, {
    select: data => data.data,
  })
}

interface EndpointsSearchQueryResult {
  filteredEndpoints: EndpointsItemsTypes[]
  status: QueryStatus
  fetchNextPage: () => void
  error: string
  hasNextPage: boolean
  isLoading: boolean
  setFilteredEndpointsState: (arg: EndpointsItemsTypes[]) => void
  refetch: () => void
}
export const GET_ENDPOINTS_QUERY_KEY = 'getEndpointsSearch'
export const useEndpointsSearchQuery = (
  filterOptions: EndpointsSearchRequest,
): EndpointsSearchQueryResult => {
  const [filteredEndpoints, setFilteredEndpoints] = useState<
    EndpointsItemsTypes[]
  >([])
  const {
    data: endpointsSearchRes,
    status,
    fetchNextPage,
    error,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: [GET_ENDPOINTS_QUERY_KEY, filterOptions],
    queryFn: ({pageParam}) =>
      EndpointsService.getEndpointsSearch({
        ...filterOptions,
        after: pageParam,
      }),
    getNextPageParam: lastPage => lastPage?.data?.data?.pageInfo?.endCursor,
  })

  // selectors
  useEffect(() => {
    const newEndpoints =
      endpointsSearchRes?.pages?.flatMap(page => {
        return transformEndpointsListResponse(page?.data?.data?.endpoints || [])
      }) || []
    setFilteredEndpoints(newEndpoints)
  }, [endpointsSearchRes?.pages])

  const hasNextPage = useMemo(() => {
    const lastPage = endpointsSearchRes?.pages?.at(-1)
    return Boolean(lastPage?.data?.data?.pageInfo?.hasNextPage)
  }, [endpointsSearchRes?.pages])

  const setFilteredEndpointsState = (
    endpoints: EndpointsItemsTypes[],
  ): void => {
    setFilteredEndpoints(endpoints)
  }

  return {
    filteredEndpoints,
    status,
    fetchNextPage,
    error: error as string,
    hasNextPage,
    isLoading,
    setFilteredEndpointsState,
    refetch,
  }
}
